package com.milta.core.localization

/**
 * Strings for the Photo/PDF/Excel import flow — analysis, result review, retry (DOM-02, `ImportScreen`).
 */
data class ImportsStrings(
    val titlePhoto: String,
    val titlePdf: String,
    val titleSpreadsheet: String,
    val fileSentSuffix: String,
    val fileWaitingConnectionLabel: String,
    // ── Écran A — analyse en cours ──
    val analysingTitle: String,
    val analysingSubtitle: String,
    /** MIL-26 — same "Analyse en cours" visual as [analysingSubtitle], mentions Claude Haiku (native PDF text or visual OCR on a scan). */
    val analysingSubtitlePdf: String,
    val stepFileSent: String,
    val stepExtracting: String,
    val stepReview: String,
    // ── Écran B — import réussi ──
    val successTitle: String,
    val successSubtitle: String,
    val addToRouteButton: String,
    val scanAnotherButton: String,
    // ── Écran C — informations incomplètes ──
    val partialTitle: String,
    val partialSubtitle: String,
    val missingFieldPlaceholder: String,
    val partialBannerText: String,
    val retryFileButton: String,
    val validateAndContinueButton: String,
    // ── Écran D — échec, nouvelle tentative possible ──
    val errorTitleUnreadable: String,
    val errorTitleMissingFields: String,
    val errorTitleNetworkError: String,
    val errorTitleGeneric: String,
    val errorSubtitle: String,
    val errorBannerSuffixUnreadable: String,
    val errorBannerSuffixMissingFields: String,
    val errorBannerSuffixNetworkError: String,
    val errorBannerSuffixGeneric: String,
    val retryWithNewFileButton: String,
    val manualEntryButton: String,
    val newDocumentButton: String,
    // ── Écran F — service d'analyse indisponible (503) ──
    val serviceUnavailableTitle: String,
    val serviceUnavailableSubtitle: String,
    val serviceUnavailableBanner: String,
    // ── Écran G — hors ligne ──
    val offlineTitle: String,
    val offlineSubtitle: String,
    val offlineBanner: String,
    val retryOfflineButton: String,
    // ── 400 UnsupportedImportFormatException (pas d'écran dédié — réutilise le bandeau de l'écran D) ──
    val unsupportedFormatBanner: String,
    // ── MIL-26 — POST /imports/pdf : écran 15 (titre) et rejets 400 avant envoi ──
    /** Navbar title of écran 15 ("Vérifier les livraisons") — distinct from [com.milta.core.localization.OcrScanStrings.reviewTitle] ("Vérifier les données") even though the rest of the screen's vocabulary is factored onto [com.milta.core.localization.OcrScanStrings] per the mockup's own note. */
    val validationTitle: String,
    val fileRejectedTitle: String,
    val fileRejectedSubtitle: String,
    val bannerFileRequired: String,
    val bannerInvalidPdfFormat: String,
    val bannerFileTooLarge: String,
    val chooseAnotherFileButton: String,
    // ── Imports asynchrones — carte d'analyse et cartes d'erreur ──
    /** Replaces CommonStrings.processingUsualDuration on the analysis card: the analysis runs server-side. */
    val processingCanLeaveNote: String,
    // ── Envoi et annulation (maquette « Envoi photo et annulation ») ──
    /** Step label while the photo is being sent (before the 202). */
    val uploadingPhotoStep: String,
    /** Step label while a PDF or spreadsheet is being sent. */
    val uploadingFileStep: String,
    /** First step once the photo was received (photo scan). */
    val photoSentStep: String,
    /** Upload progress line: "%1" = bytes sent, "%2" = total size (already formatted). */
    val uploadProgress: String,
    /** Banner under the steps while the photo is being sent. */
    val uploadingPhotoBanner: String,
    /** Banner under the steps while a PDF or spreadsheet is being sent. */
    val uploadingFileBanner: String,
    /** Only active button while sending: really cuts the upload. */
    val cancelUploadButton: String,
    /** Analysis and review screens: opens the cancel confirmation. */
    val cancelImportButton: String,
    val cancelImportDialogTitle: String,
    /** Confirmation during a photo scan analysis. */
    val cancelImportAnalysingScanMessage: String,
    /** Confirmation during a PDF or spreadsheet analysis. */
    val cancelImportAnalysingFileMessage: String,
    val cancelImportConfirmButton: String,
    val continueAnalysisButton: String,
    /** Confirmation on a review screen. Contains a "%s" placeholder replaced by the number of detected deliveries (2 or more). */
    val cancelImportReviewMessage: String,
    /** Same as cancelImportReviewMessage for a single detected delivery. */
    val cancelImportReviewMessageSingle: String,
    val continueReviewButton: String,
    /** Back on the import menu (and the web import page) after any cancel. */
    val importCancelledNotice: String,
    /** Service failure: the file is kept server-side, "Réessayer" relaunches it (no attempt limit, the AI credit counts). */
    val serviceUnavailableRetryBanner: String,
    /** Service failure the server will not relaunch: "Réessayer" is replaced by "Choisir un autre fichier". */
    val serviceUnavailableExhaustedBanner: String,
    val errorTitleTooLong: String,
    val errorSubtitleTooLong: String,
    /** Shown after "{fileName} — ", like [errorBannerSuffixUnreadable]. */
    val errorBannerSuffixTooLong: String,
    val errorTitleTooLarge: String,
    val errorSubtitleTooLarge: String,
    val errorBannerSuffixTooLarge: String,
    val errorTitleNoDeliveries: String,
    val errorSubtitleNoDeliveries: String,
    val errorBannerSuffixNoDeliveries: String,
    val errorTitleSourceExpired: String,
    val errorSubtitleSourceExpired: String,
    val errorBannerSuffixSourceExpired: String,
    /** Import cancelled server-side because its origin route was deleted (`error.import.origin_route_deleted`) — no retry. */
    val errorTitleOriginRouteDeleted: String,
    val errorSubtitleOriginRouteDeleted: String,
    val errorBannerSuffixOriginRouteDeleted: String,
    // ── Imports asynchrones — section « Import en cours » du menu d'import ──
    val activeImportHeader: String,
    val activeImportAnalysingPill: String,
    val activeImportFailedPill: String,
    val activeImportReadyPill: String,
    val activeImportRunningNote: String,
    val activeImportFailedNote: String,
    val activeImportReadyNote: String,
    /** `%s` = route day and date, e.g. "lundi 27/10" ([weekdayNamesInText] + dd/MM). */
    val activeImportRunningOtherRouteNote: String,
    /** `%s` = route day and date. */
    val activeImportReadyOtherRouteNote: String,
    /** `%s` = route day and date. */
    val activeImportFailedOtherRouteNote: String,
    val activeImportRunningNewRouteNote: String,
    val activeImportReadyNewRouteNote: String,
    val activeImportFailedNewRouteNote: String,
    /** Link to the import's origin route — `%s` = route day and date. */
    val activeImportRouteLink: String,
    /** Link to the new route being created that the import belongs to. */
    val activeImportNewRouteLink: String,
    /** Day names as written inside a sentence (Mon..Sun, index 0 = Monday) — lower case in French, "Montag" in German. */
    val weekdayNamesInText: List<String>,
)
